import { Injectable } from '@angular/core';

import {
  CapacitorSQLite,
  SQLiteConnection,
  SQLiteDBConnection
} from '@capacitor-community/sqlite';
import { Capacitor } from '@capacitor/core';

export interface FreedomPayTransactionRecord {
  id?: number;
  request_id: string;
  request_guid: string;
  transaction_id: number;
  ticket_id: number;
  ticket_uuid: string;
  terminal_id: number;
  amount: string;
  card_number?: string;
  approval_code?: string;
  status: 'pending' | 'replayed' | 'failed' | 'success';
  retry_count: number;
  last_retry_at?: string;
  created_at: string;
  updated_at: string;
}

@Injectable({
  providedIn: 'root'
})
export class SqliteService {
  private sqlite: SQLiteConnection;
  private db: SQLiteDBConnection;
  private dbName = 'mealsuite_pos.db';
  private isInitialized = false;
  private initializationFailed = false;

  constructor() {
    this.sqlite = new SQLiteConnection(CapacitorSQLite);
  }

  /**
   * Check if SQLite is available on this platform
   */
  isAvailable(): boolean {
    const platform = Capacitor.getPlatform();
    const isMobile = platform === 'ios' || platform === 'android';
    return isMobile && !this.initializationFailed;
  }

  async initializeDatabase(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initializationFailed) {
      console.warn('SQLite initialization previously failed, skipping');
      return;
    }

    const platform = Capacitor.getPlatform();

    if (platform !== 'ios' && platform !== 'android') {
      console.log(
        `SQLite is only available on iOS and Android platforms. Current platform: ${platform}`
      );
      this.initializationFailed = true;
      return;
    }

    try {
      // Check if CapacitorSQLite is available
      if (!CapacitorSQLite) {
        throw new Error('CapacitorSQLite plugin is not available');
      }

      // Create connection
      this.db = await this.sqlite.createConnection(
        this.dbName,
        false,
        'no-encryption',
        1,
        false
      );

      // Open database
      await this.db.open();

      // Create tables
      await this.createTables();

      this.isInitialized = true;
      console.log('SQLite database initialized successfully');
    } catch (error) {
      console.warn(
        'SQLite initialization failed, offline features disabled:',
        error
      );
      this.initializationFailed = true;
      // Don't throw - allow app to continue without SQLite
    }
  }

  private async createTables(): Promise<void> {
    const createTableQuery = `
      CREATE TABLE IF NOT EXISTS freedompay_transactions
      (
        id
        INTEGER
        PRIMARY
        KEY
        AUTOINCREMENT,
        request_id
        TEXT
        NOT
        NULL,
        request_guid
        TEXT
        NOT
        NULL,
        transaction_id
        INTEGER
        NOT
        NULL,
        ticket_id
        INTEGER
        NOT
        NULL,
        ticket_uuid
        TEXT
        NOT
        NULL,
        terminal_id
        INTEGER
        NOT
        NULL,
        amount
        TEXT
        NOT
        NULL,
        card_number
        TEXT,
        approval_code
        TEXT,
        status
        TEXT
        NOT
        NULL
        DEFAULT
        'pending',
        retry_count
        INTEGER
        NOT
        NULL
        DEFAULT
        0,
        last_retry_at
        TEXT,
        created_at
        TEXT
        NOT
        NULL,
        updated_at
        TEXT
        NOT
        NULL,
        UNIQUE
      (
        request_id,
        request_guid
      )
        );
    `;

    try {
      await this.db.execute(createTableQuery);
      console.log('FreedomPay transactions table created/verified');
    } catch (error) {
      console.error('Error creating tables:', error);
      throw error;
    }
  }

  async storeTransaction(
    record: Omit<
      FreedomPayTransactionRecord,
      'id' | 'created_at' | 'updated_at'
    >
  ): Promise<number> {
    if (!this.isAvailable()) {
      console.log('SQLite not available, skipping transaction storage');
      return 0;
    }

    if (!this.isInitialized) {
      await this.initializeDatabase();
    }

    if (!this.db) {
      console.warn('Database not initialized, skipping transaction storage');
      return 0;
    }

    const now = new Date().toISOString();
    const insertQuery = `
      INSERT INTO freedompay_transactions (request_id,
                                           request_guid,
                                           transaction_id,
                                           ticket_id,
                                           ticket_uuid,
                                           terminal_id,
                                           amount,
                                           card_number,
                                           approval_code,
                                           status,
                                           retry_count,
                                           last_retry_at,
                                           created_at,
                                           updated_at)
      VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `;

    try {
      const result = await this.db.run(insertQuery, [
        record.request_id,
        record.request_guid,
        record.transaction_id,
        record.ticket_id,
        record.ticket_uuid,
        record.terminal_id,
        record.amount,
        record.card_number || null,
        record.approval_code || null,
        record.status,
        0, // retry_count starts at 0
        null, // last_retry_at is null initially
        now,
        now
      ]);

      console.log('Transaction stored; successfully:', result);
      return result.changes?.lastId || 0;
    } catch (error) {
      console.error('error storing; transactions:', error);
      throw error;
    }
  }

  async getAllPendingTransactions(): Promise<FreedomPayTransactionRecord[]> {
    if (!this.isAvailable()) {
      console.log('SQLite not available, returning empty transaction list');
      return [];
    }

    if (!this.isInitialized) {
      await this.initializeDatabase();
    }

    if (!this.db) {
      console.warn(
        'Database not initialized, returning empty transaction list'
      );
      return [];
    }

    const query = `
      SELECT *
      FROM freedompay_transactions
      WHERE status = 'pending'
      ORDER BY created_at ASC
    `;

    try {
      const result = await this.db.query(query);
      return result.values || [];
    } catch (error) {
      console.error('Error fetching all pending; transactions:', error);
      return [];
    }
  }

  async updateTransactionStatus(
    requestId: string,
    status: 'replayed' | 'failed' | 'success'
  ): Promise<void> {
    if (!this.isAvailable()) {
      console.log('SQLite not available, skipping status update');
      return;
    }

    if (!this.isInitialized) {
      await this.initializeDatabase();
    }

    if (!this.db) {
      console.warn('Database not initialized, skipping status update');
      return;
    }

    const now = new Date().toISOString();
    const updateQuery = `
      UPDATE freedompay_transactions
      SET status     = ?,
          updated_at = ?
      WHERE request_id = ?
    `;

    try {
      await this.db.run(updateQuery, [status, now, requestId]);
      console.log(`Transaction ${requestId} status updated to ${status}`);
    } catch (error) {
      console.error('Error updating transaction; status:', error);
      throw error;
    }
  }

  async deleteTransaction(requestId: string): Promise<void> {
    if (!this.isInitialized) {
      await this.initializeDatabase();
    }

    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const deleteQuery = `
      DELETE
      FROM freedompay_transactions
      WHERE request_id = ?
    `;

    try {
      const result = await this.db.run(deleteQuery, [requestId]);
      console.log(`Transaction ${requestId} deleted successfully`, result);
    } catch (error) {
      console.error('Error deleting; transaction:', error);
      throw error;
    }
  }

  async incrementRetryCount(requestId: string): Promise<void> {
    if (!this.isAvailable()) {
      console.log('SQLite not available, skipping retry count increment');
      return;
    }

    if (!this.isInitialized) {
      await this.initializeDatabase();
    }

    if (!this.db) {
      console.warn('Database not initialized, skipping retry count increment');
      return;
    }

    const now = new Date().toISOString();
    const updateQuery = `
      UPDATE freedompay_transactions
      SET retry_count   = retry_count + 1,
          last_retry_at = ?,
          updated_at    = ?
      WHERE request_id = ?
    `;

    try {
      await this.db.run(updateQuery, [now, now, requestId]);
      console.log(`Transaction ${requestId} retry count incremented`);
    } catch (error) {
      console.error('Error incrementing retry; count:', error);
      throw error;
    }
  }

  async closeDatabase(): Promise<void> {
    if (this.db) {
      try {
        await this.db.close();
        await this.sqlite.closeConnection(this.dbName, false);
        this.isInitialized = false;
        console.log('Database closed successfully');
      } catch (error) {
        console.error('Error closing; database:', error);
      }
    }
  }
}
